import type { ImagePostModalProps } from "@circle-react/components/Modals/ImagePostModal/types";
import { CurrentPostContextProvider } from "@circle-react/contexts/currentPostContext";
import { CurrentSpaceContextProvider } from "@circle-react/contexts/currentSpaceContext";
import { PostsDetailsContextProvider } from "@circle-react/contexts/postsDetailsContext";
import { noop } from "lodash";

import { t } from "@/i18n-js/instance";

import { AdjustImagesComponent } from "./AdjustImages/AdjustImagesComponent";
import { STEPS } from "./constants";
import { CreatePostComponent } from "./CreatePost/CreatePostComponent";
import { useInitializeStore } from "./hooks/useInitializeStore";
import { usePost } from "./hooks/usePost";
import { useImagePostModalStore } from "./store";
import { UploadImagesComponent } from "./UploadImages/UploadImagesComponent";

interface ImagePostModalComponentProps extends ImagePostModalProps {
  onOpenChange: (open: boolean) => void;
  open: boolean;
}

export const ImagePostModalComponent = ({
  spaceId,
  open,
  onOpenChange,
  onClose = noop,
  currentPost,
  onPostChange = noop,
  shouldSkipRedirectAfterSubmit = false,
}: ImagePostModalComponentProps) => {
  const { currentStep, setCurrentStep, reset, isFormDirty, isImagesDirty } =
    useImagePostModalStore();
  const onModalClose = (skipDirtyCheck: boolean = false) => {
    if (
      skipDirtyCheck ||
      !(isFormDirty || isImagesDirty) ||
      confirm(t("quick_post_v2.accidental_leave_confirmation"))
    ) {
      reset();
      onOpenChange(false);
      onClose();
    }
  };
  const post = usePost(currentPost);

  useInitializeStore(post, open);

  if (!open || !post) return null;

  const perPagePostIds = post.id ? [[post.id]] : [];

  return (
    <CurrentSpaceContextProvider spaceId={spaceId}>
      <PostsDetailsContextProvider perPagePostIds={perPagePostIds}>
        <CurrentPostContextProvider post={post}>
          {currentStep === STEPS.UPLOAD_IMAGES && (
            <UploadImagesComponent
              open={open}
              onClose={onModalClose}
              onSelection={() => setCurrentStep(STEPS.ADJUST_IMAGES)}
            />
          )}

          {currentStep === STEPS.ADJUST_IMAGES && (
            <AdjustImagesComponent open={open} onClose={onModalClose} />
          )}

          {currentStep !== STEPS.UPLOAD_IMAGES &&
            currentStep !== STEPS.ADJUST_IMAGES && (
              <CreatePostComponent
                open={open}
                onClose={onModalClose}
                onPostChange={onPostChange}
                shouldSkipRedirectAfterSubmit={shouldSkipRedirectAfterSubmit}
              />
            )}
        </CurrentPostContextProvider>
      </PostsDetailsContextProvider>
    </CurrentSpaceContextProvider>
  );
};
